import { useAuth0 } from '@auth0/auth0-react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnapshot } from 'valtio';
import { AppPages } from '../../common/routing/app-navigator';
import { loadConnectorsByFamily } from '../../connectors/api/connectors-api';
import {
  closeSkipOnboarding,
  initOnboarding,
  isOnboardingRequired,
  showSkipOnBoarding,
  skipOnboarding,
} from '../actions/onboarding-actions';
import {
  setChosenCodeRepo,
  setOnboardingStep,
} from '../store-actions/onboarding-store-actions';
import { OnboardingError } from '../components/OnboardingError';
import { OnboardingSteps } from '../onboarding-types';
import OnboardingStore from '../stores/onboarding-store';
import OnboardingConnectorsContainer from './OnboardingConnectorsContainer';
import OnboardingDiscoveryContainer from './OnboardingDiscoveryContainer';
import OnboardingAppSelectionContainer from './OnboardingAppSelectionContainer';
import { initRepoSelectionStep } from '../actions/onboarding-repo-selection-actions';
import OnboardingDialog from '../components/OnboardingDialog';
import RepoSelectionStore from '../stores/onboarding-repo-selection-store';
import { datadogLogs } from '@datadog/browser-logs';
import { useScan } from '../../new-scan/actions/scan-actions';
import {
  getHubSpotFieldForSourceControl,
  submitHubSpotForm,
} from '../../hubspot-analytics/actions/hubspot-analytics-actions';
import {
  HubSpotOxAppFormFields,
  HubSpotFormsIds,
  HubSpotOxBaseFormFields,
} from '../../hubspot-analytics/hubspot-types';
import { logger } from 'logging-utils';

const OnboardingContainer = () => {
  const { user } = useAuth0();
  const navigate = useNavigate();
  const {
    isOnboardingInitInProcess,
    codeRepoConnectors,
    generalError,
    errorMessage,
    onboardingStep,
    chosenCodeRepo,
    isSkipModalVisible,
  } = useSnapshot(OnboardingStore);
  const {
    checkedReposUtils: { reposFound },
  } = useSnapshot(RepoSelectionStore);
  const { maxReposToScan } = useScan();

  useEffect(() => {
    if (user && !user.org_id && !isOnboardingInitInProcess) {
      setOnboardingStep(OnboardingSteps.Connect);
      initOnboarding(user);
    } else if (user && user.org_id && !codeRepoConnectors) {
      datadogLogs.logger.info(
        `Onboarding: fetching connectors for onboarding`,
        { user },
      );
      loadConnectorsByFamily();
    } else if (
      user &&
      user.org_id &&
      codeRepoConnectors &&
      codeRepoConnectors.length > 0
    ) {
      const onboardingRequired = isOnboardingRequired(codeRepoConnectors);
      if (!onboardingRequired && !onboardingStep) {
        navigate(AppPages.Home);
      } else if (onboardingRequired && !onboardingStep) {
        datadogLogs.logger.info(
          'Onboarding: setting onboarding step to "Connectors"',
          { user },
        );
        setOnboardingStep(OnboardingSteps.Connect);
      }
    }
  }, [
    codeRepoConnectors,
    isOnboardingInitInProcess,
    navigate,
    onboardingStep,
    user,
  ]);

  useEffect(() => {
    if (!chosenCodeRepo && !codeRepoConnectors) {
      return;
    } else if (!chosenCodeRepo && codeRepoConnectors) {
      const configuredConnector = codeRepoConnectors?.find(
        connector => connector.isConfigured,
      );
      if (!configuredConnector) {
        setOnboardingStep(OnboardingSteps.Connect);
        return;
      }
      setChosenCodeRepo(configuredConnector);
      return;
    }
    if (onboardingStep === OnboardingSteps.ChooseRepos && chosenCodeRepo) {
      datadogLogs.logger.info('Onboarding: Repo selection step initializing');
      const sourceControlField = getHubSpotFieldForSourceControl(
        chosenCodeRepo.displayName,
      );
      if (user) {
        if (!sourceControlField) {
          logger.error(
            `Could not find source control field for ${chosenCodeRepo.displayName}`,
          );
        } else {
          submitHubSpotForm(
            {
              [HubSpotOxBaseFormFields.Email]: user.email!,
              [sourceControlField]: true,
              [HubSpotOxBaseFormFields.FirstName]: user.given_name || user.name,
              [HubSpotOxBaseFormFields.LastName]: user.family_name,
              [HubSpotOxAppFormFields.DemoDataRequested]: false,
            },
            HubSpotFormsIds.OxAppForm,
          );
        }
      }
      initRepoSelectionStep(chosenCodeRepo.id);
    }
  }, [chosenCodeRepo, codeRepoConnectors, onboardingStep, user]);

  if (generalError) {
    return (
      <OnboardingError errorMsg={errorMessage || ''} isOpen={generalError} />
    );
  }
  const modalHeader =
    onboardingStep === OnboardingSteps.Connect && !isOnboardingInitInProcess
      ? 'Choose code repository'
      : undefined;
  const shouldDisableSkip =
    !!maxReposToScan && !!reposFound && reposFound > maxReposToScan;
  return (
    <OnboardingDialog
      header={modalHeader}
      open={!!onboardingStep}
      closeIcon={
        !(shouldDisableSkip && onboardingStep === OnboardingSteps.ChooseRepos)
      }
      handleSkipOnboarding={skipOnboarding}
      closeSkipOnboarding={closeSkipOnboarding}
      isSkipOnboardingOpen={isSkipModalVisible}
      showSkipOnBoarding={showSkipOnBoarding}>
      {onboardingStep === OnboardingSteps.Connect && (
        <OnboardingConnectorsContainer
          connectors={codeRepoConnectors || []}
          creatingOrgName={user ? `${user?.nickname} Org` : ''}
        />
      )}
      {onboardingStep === OnboardingSteps.ChooseRepos && (
        <OnboardingAppSelectionContainer />
      )}
      {onboardingStep === OnboardingSteps.Discovery && (
        <OnboardingDiscoveryContainer />
      )}
    </OnboardingDialog>
  );
};

export default OnboardingContainer;
